import * as Linking from 'expo-linking';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonVariant,
    CircleXIcon,
    EditIcon,
    FullScreenSpinner,
    H2Emphasized,
    H6Regular,
    Image,
    isNonMobileWeb,
    isWeb,
    PageBelowHeader,
    SafeAreaView,
    ScrollView,
    useThemeColors,
    VideoIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import {
    getFormattedStartTime,
    getValidSessionBookingDoneRoute,
    sessionHasEnded,
    sessionIsInProgress,
    sessionStartTimeIsWithinNMinutes,
} from '../helpers';
import { useGetGuidanceSessions } from '../hooks/useGetGuidanceSessions';
import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import { sessionForDetailsViewAtom } from '../state/GuidanceSessionsAtoms';
import { GuidanceSessionType } from '../types/GuidanceSessions';
import CancelSessionSheet from './CancelSessionSheet';

const SessionDetails = () => {
    const { trackViewOnce } = useViewEvents();
    const { getColor } = useThemeColors();
    const { membership } = useAuthedUser();
    const { getSessionDetails } = useMembershipGuidanceSessionInfo();
    const { guide } = membership ?? {};

    let session = useAtomValue(sessionForDetailsViewAtom);

    const { upcomingSessions, isLoadingSessions: isLoadingUpcomingSessions } =
        useGetGuidanceSessions('upcoming');
    const [cancelSheetIsOpen, setCancelSheetIsOpen] = useState(false);

    // This component should normally use the sessionForDetailsViewAtom to load the guidance session into the view, particularly when navigating in-app.
    // However, we also support linking into the session details page from an external source, so we need to handle the case where the atom value is not set.
    // In that case, we expect the query param guidanceSessionId to be set and filter upcoming sessions to find the session.
    const { guidanceSessionId, doneRoute } = useLocalSearchParams<{
        guidanceSessionId?: string;
        doneRoute?: string;
    }>();
    const validDoneRoute = getValidSessionBookingDoneRoute(doneRoute as string);

    if (
        guidanceSessionId != null &&
        upcomingSessions != null &&
        upcomingSessions.length > 0
    ) {
        session =
            upcomingSessions?.find(
                (session) => session.id === guidanceSessionId,
            ) ?? null;
    }

    if (session == null && isLoadingUpcomingSessions) {
        return <FullScreenSpinner />;
    }

    if (!session) {
        return <Redirect href={validDoneRoute} />;
    }

    trackViewOnce('GuidanceSessions/SessionDetails');

    const sessionDetails = getSessionDetails(session.type);
    const {
        startTime,
        videoConferenceUrl,
        calendlyCancelUrl,
        calendlyRescheduleUrl,
        focus,
    } = session;
    const { title, description, image } = sessionDetails;

    const sessionStartsSoon = sessionStartTimeIsWithinNMinutes(session, 10);
    const sessionInProgress = sessionIsInProgress(session);
    const sessionAlreadyHappened = sessionHasEnded(session);
    const isJoinable = sessionStartsSoon || sessionInProgress;

    return (
        <SafeAreaView
            edges={['top', 'left', 'right']}
            noBottomPadding={true}
            style={{
                backgroundColor: isJoinable
                    ? getColor('$green300')
                    : getColor('$peach300'),
                ...(isWeb ? { flex: 1 } : {}),
            }}
        >
            <YStack
                height="100%"
                maxHeight="40%"
                $sm={{ minHeight: 40, height: 'auto' }}
            >
                <YStack
                    marginHorizontal={16}
                    marginTop={16}
                    justifyContent="center"
                    flexGrow={1}
                    flexBasis="45%"
                    $sm={{ flexBasis: '10%' }}
                >
                    {isNonMobileWeb ? (
                        <BackButton
                            fallbackRoute={validDoneRoute}
                            iconType={BackButtonIconType.ARROW_LEFT}
                        />
                    ) : (
                        <>
                            <Image
                                source={image}
                                width={180}
                                height={180}
                                $mobile_mini={{ height: 130, width: 130 }}
                                resizeMode="contain"
                                marginHorizontal="auto"
                            />
                            <XStack position="absolute" top={0} left={0}>
                                <BackButton
                                    fallbackRoute={validDoneRoute}
                                    iconType={BackButtonIconType.ARROW_LEFT}
                                />
                            </XStack>
                        </>
                    )}
                </YStack>
                <XStack
                    alignItems="flex-end"
                    justifyContent="space-between"
                    padding={20}
                    width="100%"
                    maxWidth={700}
                    marginHorizontal="auto"
                    $sm={{ paddingHorizontal: 0 }}
                >
                    <YStack>
                        {sessionAlreadyHappened ? (
                            <H6Regular>
                                {title} session - Completed on
                            </H6Regular>
                        ) : (
                            <H6Regular>{title} session</H6Regular>
                        )}
                        <H2Emphasized marginVertical={6}>
                            {getFormattedStartTime(startTime)}
                        </H2Emphasized>
                    </YStack>
                    {isNonMobileWeb && (
                        <Image
                            source={image}
                            width={110}
                            height={110}
                            resizeMode="contain"
                        />
                    )}
                </XStack>
            </YStack>
            <PageBelowHeader paddingHorizontal={0} paddingBottom={0} flex={1}>
                <ScrollView
                    contentContainerStyle={{
                        width: '100%',
                        maxWidth: 700,
                        marginHorizontal: 'auto',
                        paddingHorizontal: 20,
                        paddingBottom: 32,
                    }}
                >
                    {!sessionAlreadyHappened && (
                        <YStack width="100%" gap={24} paddingTop={8}>
                            {isJoinable && (
                                <XStack>
                                    <Button
                                        variant={ButtonVariant.PRIMARY}
                                        tone="positive"
                                        icon={VideoIcon}
                                        text="Join the session"
                                        onPress={() => {
                                            void Linking.openURL(
                                                videoConferenceUrl,
                                            );
                                        }}
                                    />
                                </XStack>
                            )}
                            <XStack gap={isNonMobileWeb ? 24 : 12} width="100%">
                                {typeof calendlyRescheduleUrl === 'string' && (
                                    <View
                                        flex={1}
                                        $platform-web={{ flexBasis: '50%' }}
                                    >
                                        <Button
                                            variant={ButtonVariant.SECONDARY}
                                            icon={EditIcon}
                                            text="Reschedule"
                                            onPress={() => {
                                                if (
                                                    session.type ===
                                                    GuidanceSessionType.DISCOVERY
                                                ) {
                                                    router.navigate(
                                                        '/onboarding/book-first-session',
                                                    );
                                                } else {
                                                    router.navigate({
                                                        pathname: `/guidance-sessions/reschedule-session`,
                                                        params: {
                                                            rescheduleUrl:
                                                                encodeURI(
                                                                    calendlyRescheduleUrl,
                                                                ),
                                                            doneRoute:
                                                                validDoneRoute as string,
                                                        },
                                                    });
                                                }
                                            }}
                                        />
                                    </View>
                                )}
                                {typeof calendlyCancelUrl === 'string' && (
                                    <View
                                        flex={1}
                                        $platform-web={{ flexBasis: '50%' }}
                                    >
                                        <Button
                                            variant={ButtonVariant.SECONDARY}
                                            icon={CircleXIcon}
                                            text="Cancel"
                                            onPress={() => {
                                                setCancelSheetIsOpen(true);
                                            }}
                                        />
                                    </View>
                                )}
                            </XStack>

                            {description != null && (
                                <YStack gap={8} width="100%">
                                    <BodyMediumEmphasized>
                                        About this session
                                    </BodyMediumEmphasized>
                                    <BodyMediumRegular>
                                        {description}
                                    </BodyMediumRegular>
                                </YStack>
                            )}
                        </YStack>
                    )}

                    {focus != null && (
                        <YStack marginVertical={24} gap={8} width="100%">
                            <BodyMediumEmphasized>
                                Your note to {guide?.firstName}
                            </BodyMediumEmphasized>
                            <BodyMediumRegular>{focus}</BodyMediumRegular>
                        </YStack>
                    )}
                </ScrollView>
            </PageBelowHeader>
            <CancelSessionSheet
                session={session}
                isOpen={cancelSheetIsOpen}
                onClose={() => {
                    setCancelSheetIsOpen(false);
                }}
                doneRoute={validDoneRoute}
            />
        </SafeAreaView>
    );
};

export default SessionDetails;
